import React, { useState } from 'react';
import { Modal, Button, Form, Badge, ProgressBar, Alert } from 'react-bootstrap';
import api from '../services/api';

const CHECKLIST_ITEMS = [
  [1,  "Language Display – Ensure language-specific questions appear correctly for each country"],
  [2,  "Panel ID Capture – Confirm the panel ID is recorded at the end of the survey"],
  [3,  "Redirect Logic – For large samples with multiple redirects, verify all redirects are correctly implemented"],
  [4,  "Auto-codes Hidden – Make sure all auto-code variables are hidden from respondents"],
  [5,  "Expression Questions Visible – Don't hide questions with calculated expressions (S2G only)"],
  [6,  "Randomization Exceptions – 'None of these' and 'Other—please specify' options should remain fixed and not randomized"],
  [7,  "Attributes and logos are readable – Both Topics and Answers (especially in mobile view)"],
  [8,  "Scales are properly labelled – Labels need ratings like 0-Extremely poor, 10-Excellent (Negative to Positive)"],
  [9,  "Piping has to be checked as per the questionnaire logic"],
  [10, "Sequential Question Numbering – Check that question numbers are consecutive and logical"],
  [11, "Clear Variable Names – Ensure all variables are named intuitively"],
  [12, "Display Logic Verification – Test every display logic scenario thoroughly"],
  [13, "'Not Coded' Logic – If display logic includes a 'Not Coded' condition, ensure it's applied only when that question appears"],
  [14, "Complex Logic Review – Any complex or new logic should be reviewed and approved with DP personnel and senior stakeholders"],
  [15, "Termination Logic – Verify all termination conditions (quota full, disqualify) work as intended"],
  [16, "Formatting & Translations – Confirm bold, italics, colors, and formatting (including translations) match the questionnaire"],
  [17, "Flag Implementation – Ensure flags like flagByDuration, flagByFakeGPS, flagStraightlining, flagShortOpenEnded, and flagTooManySessions are correctly enabled"],
  [18, "Numeric Input Validation – Text boxes for numeric questions must accept only numbers"],
  [19, "Media Display – Check that concept/product/ad images display properly on both desktop and mobile"],
  [20, "Arabic Punctuation – In Arabic translations, ensure spaces before and after hyphens are correctly formatted"],
  [21, "Redirect & Termination Review – Test each redirect, termination, and completion scenario"],
  [22, "Quota Variable Auto-codes – Confirm all quota variables are auto-coded properly"],
  [23, "Quota Count Validation – Verify quota counts match the specifications (check order of attributes, do not random copy-paste)"],
  [24, "Cleared Test Records"],
  [25, "Shared Monitoring links / Assigned with researchers / FW Team / Enable supervisor access"],
  [26, "Enquired about adding dummy question for QC purpose"],
  [27, "Termination tracking is enabled"],
  [28, "Survey is taken and completed till the end for all languages before live"],
];

const CHECK_OPTIONS = ['Yes', 'No', 'NA'];
const CHECK_COLORS = { Yes: 'success', No: 'danger', NA: 'secondary' };

const ChecklistModal = ({ show, onHide, segment, statusStage, onSubmitted, previouslySkipped }) => {
  const [responses, setResponses] = useState({});
  const [skipping, setSkipping] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const stageLabel = statusStage === 'test_link_delivered' ? 'Test Link Delivered' : 'Final Link Delivered';

  const answeredCount = Object.keys(responses).filter(k => responses[k]?.check).length;
  const progress = Math.round((answeredCount / CHECKLIST_ITEMS.length) * 100);

  const setResponse = (num, field, value) => {
    setResponses(prev => ({
      ...prev,
      [num]: { ...(prev[num] || {}), [field]: value },
    }));
  };

  const handleSubmit = async () => {
    setError('');
    const unanswered = CHECKLIST_ITEMS.filter(([num]) => !responses[num]?.check);
    if (unanswered.length > 0) {
      setError(`Please answer all items before submitting (${unanswered.length} remaining).`);
      return;
    }
    setSubmitting(true);
    try {
      await api.post('api/checklists/save/', {
        segment_id: segment.id,
        status_stage: statusStage,
        skipped: false,
        responses,
      }, {
        headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
      });
      onSubmitted({ skipped: false });
    } catch (e) {
      setError('Failed to save checklist. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setError('');
    if (!skipReason.trim()) {
      setError('Please provide a reason for skipping.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('api/checklists/save/', {
        segment_id: segment.id,
        status_stage: statusStage,
        skipped: true,
        skip_reason: skipReason.trim(),
        responses: {},
      }, {
        headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
      });
      onSubmitted({ skipped: true });
    } catch (e) {
      setError('Failed to save skip reason. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await api.get(`api/checklists/download/?segment_id=${segment.id}`, {
        responseType: 'blob',
        headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `Checklist_${segment.segment_name || segment.id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (_) {
      alert('No checklist data available to download yet.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static" keyboard={false} scrollable>
      <Modal.Header style={{ background: '#1a2e4a', color: '#fff' }}>
        <Modal.Title style={{ fontSize: 16, color: '#fff' }}>
          Scripting Checklist — {stageLabel}
          {previouslySkipped && (
            <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: 12 }}>
              Skipped at Test Link stage
            </Badge>
          )}
        </Modal.Title>
        <div style={{ fontSize: 13, color: '#ccc', marginLeft: 'auto', paddingLeft: 16 }}>
          {segment?.segment_name || segment?.project_name}
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: '16px 20px' }}>
        {previouslySkipped && !skipping && (
          <Alert variant="warning" style={{ fontSize: 13, padding: '8px 12px' }}>
            This checklist was skipped at the Test Link Delivered stage. Please fill it now before marking Final Link Delivered.
          </Alert>
        )}

        {error && <Alert variant="danger" style={{ fontSize: 13, padding: '8px 12px' }}>{error}</Alert>}

        {!skipping ? (
          <>
            <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
              <ProgressBar
                now={progress}
                label={`${answeredCount}/${CHECKLIST_ITEMS.length}`}
                style={{ flex: 1, height: 20 }}
                variant={progress === 100 ? 'success' : 'primary'}
              />
              <span style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>
                {answeredCount} of {CHECKLIST_ITEMS.length} answered
              </span>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ padding: '6px 8px', width: 32, textAlign: 'center' }}>#</th>
                  <th style={{ padding: '6px 8px' }}>Item</th>
                  <th style={{ padding: '6px 8px', width: 160, textAlign: 'center' }}>Response</th>
                  <th style={{ padding: '6px 8px', width: 200 }}>Comment</th>
                </tr>
              </thead>
              <tbody>
                {CHECKLIST_ITEMS.map(([num, desc]) => {
                  const r = responses[num] || {};
                  return (
                    <tr key={num} style={{ borderBottom: '1px solid #eee', background: r.check ? (r.check === 'Yes' ? '#f0fff0' : r.check === 'No' ? '#fff0f0' : '#fafafa') : '#fff' }}>
                      <td style={{ padding: '6px 8px', textAlign: 'center', color: '#888' }}>{num}</td>
                      <td style={{ padding: '6px 8px', lineHeight: 1.4 }}>{desc}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                          {CHECK_OPTIONS.map(opt => (
                            <button
                              key={opt}
                              onClick={() => setResponse(num, 'check', opt)}
                              style={{
                                padding: '2px 10px',
                                fontSize: 12,
                                border: `1px solid ${r.check === opt ? (opt === 'Yes' ? '#2e7d32' : opt === 'No' ? '#c62828' : '#757575') : '#ccc'}`,
                                borderRadius: 4,
                                background: r.check === opt ? (opt === 'Yes' ? '#2e7d32' : opt === 'No' ? '#c62828' : '#757575') : '#fff',
                                color: r.check === opt ? '#fff' : '#555',
                                cursor: 'pointer',
                                fontWeight: r.check === opt ? 'bold' : 'normal',
                              }}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <input
                          type="text"
                          placeholder="Optional comment"
                          value={r.comment || ''}
                          onChange={e => setResponse(num, 'comment', e.target.value)}
                          style={{ width: '100%', fontSize: 12, padding: '2px 6px', border: '1px solid #ddd', borderRadius: 4 }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        ) : (
          <div>
            {statusStage === 'test_link_delivered' ? (
              <p style={{ fontSize: 13, marginBottom: 8, color: '#555' }}>
                You can plan to fill this checklist when the <strong>Final Link</strong> is delivered.
                Please enter a brief note explaining why you're deferring:
              </p>
            ) : (
              <p style={{ fontSize: 13, marginBottom: 8, color: '#555' }}>
                Please provide a reason for skipping the scripting checklist at this stage:
              </p>
            )}
            <Form.Control
              as="textarea"
              rows={4}
              placeholder={
                statusStage === 'test_link_delivered'
                  ? 'e.g. Still scripting — will complete at Final Link Delivered stage'
                  : 'Enter reason for skipping...'
              }
              value={skipReason}
              onChange={e => setSkipReason(e.target.value)}
              style={{ fontSize: 13 }}
            />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ justifyContent: 'space-between' }}>
        <div>
          {!skipping ? (
            <Button
              variant={statusStage === 'test_link_delivered' ? 'outline-primary' : 'outline-warning'}
              size="sm"
              onClick={() => { setSkipping(true); setError(''); }}
              disabled={submitting}
            >
              {statusStage === 'test_link_delivered' ? 'Plan for Final Link' : 'Skip with Reason'}
            </Button>
          ) : (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => { setSkipping(false); setError(''); }}
              disabled={submitting}
            >
              Back to Checklist
            </Button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!skipping ? (
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={submitting}
              style={{ background: '#1a2e4a', borderColor: '#1a2e4a' }}
            >
              {submitting ? 'Saving...' : `Submit Checklist (${answeredCount}/${CHECKLIST_ITEMS.length})`}
            </Button>
          ) : (
            <Button
              variant={statusStage === 'test_link_delivered' ? 'primary' : 'warning'}
              size="sm"
              onClick={handleSkip}
              disabled={submitting}
              style={statusStage === 'test_link_delivered' ? { background: '#1a2e4a', borderColor: '#1a2e4a' } : {}}
            >
              {submitting ? 'Saving...' : (statusStage === 'test_link_delivered' ? 'Confirm — Plan at Final Link' : 'Confirm Skip')}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ChecklistModal;
